/**
 * Stats Command
 * Show usage statistics and session information
 */

import kleur from 'kleur';
import fs from 'fs';
import path from 'path';
import os from 'os';
import { getSessionMemory } from '../../runtime/sessionMemory';
import { getCheckpointManager } from '../../checkpoints';

interface UsageStats {
  totalSessions: number;
  totalMessages: number;
  totalTokens: number;
  favoriteModel?: string;
  lastActive?: string;
  streak?: number;
}

export async function runStats(args: string[]): Promise<void> {
  const subCommand = args[0] || 'summary';

  switch (subCommand) {
    case 'summary':
    case 'show':
      showStatsSummary();
      break;

    case 'sessions':
      showSessionStats();
      break;

    case 'checkpoints':
      showCheckpointStats();
      break;

    case 'usage':
      showUsageStats();
      break;

    case 'clear':
      clearStats();
      break;

    case 'help':
    default:
      showHelp();
  }
}

function showStatsSummary(): void {
  console.log(kleur.cyan('\nUsage Statistics'));
  console.log(kleur.gray('-'.repeat(60)));

  // Session stats
  const sessionMemory = getSessionMemory();
  const recentSessions = sessionMemory.listRecentSessions(100);

  console.log(kleur.white('Sessions'));
  console.log(kleur.gray(`  Total sessions: ${recentSessions.length}`));

  if (recentSessions.length > 0) {
    const latest = recentSessions[0];
    console.log(kleur.gray(`  Last active: ${formatTimestamp(latest.lastActivity)}`));

    // Calculate streak (consecutive days)
    const streak = calculateStreak(recentSessions);
    if (streak > 0) {
      console.log(kleur.gray(`  Current streak: ${streak} day${streak > 1 ? 's' : ''}`));
    }
  }

  console.log();

  // Checkpoint stats
  try {
    const checkpointManager = getCheckpointManager();
    const cpSummary = checkpointManager.getCheckpointsSummary();

    console.log(kleur.white('Checkpoints'));
    console.log(kleur.gray(`  Total: ${cpSummary.total}`));
    console.log(kleur.gray(`  This session: ${cpSummary.sessionCount}`));

    if (cpSummary.newestTimestamp) {
      console.log(kleur.gray(`  Latest: ${formatTimestamp(cpSummary.newestTimestamp)}`));
    }
  } catch {
    console.log(kleur.white('Checkpoints'));
    console.log(kleur.gray('  Not initialized'));
  }

  console.log();

  // Storage stats
  const storageStats = getStorageStats();
  console.log(kleur.white('Storage'));
  console.log(kleur.gray(`  Sessions: ${formatBytes(storageStats.sessions)}`));
  console.log(kleur.gray(`  Checkpoints: ${formatBytes(storageStats.checkpoints)}`));
  console.log(kleur.gray(`  Total: ${formatBytes(storageStats.total)}`));

  console.log();

  // Environment
  console.log(kleur.white('Environment'));
  console.log(kleur.gray(`  Node.js: ${process.version}`));
  console.log(kleur.gray(`  Platform: ${os.platform()} ${os.arch()}`));
  console.log(kleur.gray(`  Working directory: ${process.cwd()}`));

  console.log(kleur.gray('-'.repeat(60)));
}

function showSessionStats(): void {
  const sessionMemory = getSessionMemory();
  const recentSessions = sessionMemory.listRecentSessions(20);

  console.log(kleur.cyan('\nRecent Sessions'));
  console.log(kleur.gray('-'.repeat(60)));

  if (recentSessions.length === 0) {
    console.log(kleur.gray('No sessions found'));
    return;
  }

  for (const session of recentSessions) {
    const time = formatTimestamp(session.lastActivity);
    const task = session.currentTask
      ? session.currentTask.substring(0, 40) + (session.currentTask.length > 40 ? '...' : '')
      : 'No task';

    console.log(kleur.yellow(`  ${session.sessionId.substring(0, 20)}`));
    console.log(kleur.gray(`    Last active: ${time}`));
    console.log(kleur.gray(`    Task: ${task}`));
    console.log();
  }
}

function showCheckpointStats(): void {
  try {
    const checkpointManager = getCheckpointManager();
    const checkpoints = checkpointManager.getCheckpoints();

    console.log(kleur.cyan('\nCheckpoint Statistics'));
    console.log(kleur.gray('-'.repeat(60)));

    if (checkpoints.length === 0) {
      console.log(kleur.gray('No checkpoints found'));
      return;
    }

    // Group by trigger tool
    const byTool: Record<string, number> = {};
    for (const cp of checkpoints) {
      const tool = cp.triggerTool || 'unknown';
      byTool[tool] = (byTool[tool] || 0) + 1;
    }

    console.log(kleur.white('By Tool:'));
    for (const [tool, count] of Object.entries(byTool).sort((a, b) => b[1] - a[1])) {
      const bar = '='.repeat(Math.min(count, 30));
      console.log(kleur.gray(`  ${tool.padEnd(15)} ${bar} ${count}`));
    }

    console.log();

    // Recent checkpoints
    console.log(kleur.white('Recent Checkpoints:'));
    for (const cp of checkpoints.slice(0, 5)) {
      const time = formatTimestamp(cp.timestamp);
      console.log(kleur.gray(`  ${time}: ${cp.description}`));
    }
  } catch {
    console.log(kleur.yellow('Checkpoint manager not initialized'));
  }
}

function showUsageStats(): void {
  console.log(kleur.cyan('\nUsage Over Time'));
  console.log(kleur.gray('-'.repeat(60)));

  const sessionMemory = getSessionMemory();
  const recentSessions = sessionMemory.listRecentSessions(100);

  if (recentSessions.length === 0) {
    console.log(kleur.gray('No usage data available'));
    return;
  }

  // Group by day
  const byDay: Record<string, number> = {};
  for (const session of recentSessions) {
    const date = new Date(session.lastActivity).toISOString().split('T')[0];
    byDay[date] = (byDay[date] || 0) + 1;
  }

  // Show last 14 days
  const days = Object.entries(byDay)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 14)
    .reverse();

  console.log(kleur.white('Sessions per day (last 14 days):'));
  console.log();

  const maxCount = Math.max(...days.map(d => d[1]));
  for (const [date, count] of days) {
    const barLength = Math.round((count / maxCount) * 30);
    const bar = '='.repeat(barLength);
    const dayName = new Date(date).toLocaleDateString('en-US', { weekday: 'short' });
    console.log(kleur.gray(`  ${dayName} ${date.substring(5)} ${bar} ${count}`));
  }
}

function clearStats(): void {
  console.log(kleur.yellow('This will clear all session history and checkpoints.'));
  console.log(kleur.gray('Use /clear to clear current session context instead.'));
}

function showHelp(): void {
  console.log(kleur.cyan('\nStats Command'));
  console.log(kleur.gray('-'.repeat(60)));
  console.log(kleur.white('View usage statistics and session information'));
  console.log();
  console.log(kleur.cyan('Commands:'));
  console.log(kleur.gray('  /stats              - Show summary statistics'));
  console.log(kleur.gray('  /stats summary      - Show summary statistics'));
  console.log(kleur.gray('  /stats sessions     - Show recent sessions'));
  console.log(kleur.gray('  /stats checkpoints  - Show checkpoint statistics'));
  console.log(kleur.gray('  /stats usage        - Show usage over time'));
}

function calculateStreak(sessions: Array<{ lastActivity: string }>): number {
  if (sessions.length === 0) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let streak = 0;
  let currentDate = today;

  // Sort sessions by date descending
  const sortedDates = sessions
    .map(s => {
      const d = new Date(s.lastActivity);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    })
    .filter((v, i, a) => a.indexOf(v) === i) // unique dates
    .sort((a, b) => b - a);

  for (const timestamp of sortedDates) {
    const diff = (currentDate.getTime() - timestamp) / (1000 * 60 * 60 * 24);

    if (diff <= 1) {
      streak++;
      currentDate = new Date(timestamp);
    } else {
      break;
    }
  }

  return streak;
}

function getStorageStats(): { sessions: number; checkpoints: number; total: number } {
  const metisDir = path.join(process.cwd(), '.metis');
  let sessions = 0;
  let checkpoints = 0;

  try {
    const sessionsDir = path.join(metisDir, 'sessions');
    if (fs.existsSync(sessionsDir)) {
      sessions = getDirSize(sessionsDir);
    }
  } catch {}

  try {
    const checkpointsDir = path.join(metisDir, 'checkpoints');
    if (fs.existsSync(checkpointsDir)) {
      checkpoints = getDirSize(checkpointsDir);
    }
  } catch {}

  return {
    sessions,
    checkpoints,
    total: sessions + checkpoints,
  };
}

function getDirSize(dir: string): number {
  let size = 0;

  try {
    const files = fs.readdirSync(dir);
    for (const file of files) {
      const filePath = path.join(dir, file);
      const stats = fs.statSync(filePath);
      if (stats.isFile()) {
        size += stats.size;
      } else if (stats.isDirectory()) {
        size += getDirSize(filePath);
      }
    }
  } catch {}

  return size;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return date.toLocaleDateString();
}
